import { TCPA } from "@lgp/shared/constants";
import { assetsClient } from "@lgp/shared/utils/LGPApiClient";
import { substitutions } from "@lgp/shared/utils/string";
import { createContext, useCallback, useEffect, useState } from "react";

const contentCache = {};

const initialAppState = {
  isLoaded: false,
  parentHost: null,
  parentUrl: null,
  configData: null,
};

const initialContext = {
  appState: { ...initialAppState },
};

const getUrlHash = (url) => {
  const parts = url.split("#");
  if (parts.length > 1) {
    return parts[1];
  }

  return undefined;
};

export const appContext = createContext(initialContext);

const { Provider } = appContext;

const AppProvider = (props) => {
  const { parentHost, parentUrl, configData, children } = props;
  const [appState, setAppState] = useState({
    ...initialAppState,
    parentHost,
    parentUrl,
    configData,
  });

  const fetchContent = useCallback(
    function fetchContent(url) {
      let newUrl = url;
      if (!url.startsWith(TCPA.TCPA_S3_BASE_URL)) {
        if (url.indexOf("privacy-policy") > -1) {
          newUrl = `${TCPA.TCPA_S3_BASE_URL}/privacy-policy.html`;
        } else if (url.indexOf("consumer-tos") > -1) {
          newUrl = `${TCPA.TCPA_S3_BASE_URL}/consumer-tos.html`;
        } else {
          throw new Error(`URL: ${url} is invalid.`);
        }

        const hash = getUrlHash(url);
        if (hash) {
          newUrl += `#${hash}`;
        }
      }

      const urlObj = new URL(newUrl);
      const key = urlObj.pathname
        .replace(TCPA.TCPA_S3_DIRNAME, "")
        .replace(/\//g, "");

      if (contentCache[key]) {
        setAppState((prevState) => ({
          ...prevState,
          content: {
            ...contentCache[key],
            hash: urlObj.hash,
          },
          isLoaded: true,
          error: null,
        }));
      } else {
        assetsClient
          .makeRequest("GET", urlObj.pathname, {
            headers: {
              Accept: "text/html",
              "Content-Type": "text/html",
            },
          })
          .then((content) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, "text/html");
            const titleTags = doc.head.getElementsByTagName("title");
            contentCache[key] = {
              key,
              title:
                titleTags.length > 0
                  ? substitutions(titleTags[0].innerHTML, {
                      Company_Name: appState.configData.companyName,
                    })
                  : urlObj.pathname,
              body: doc.body.innerHTML,
            };
            setAppState((prevState) => ({
              ...prevState,
              content: { ...contentCache[key], key, hash: urlObj.hash },
              isLoaded: true,
              error: null,
            }));
          })
          .catch((error) => {
            setAppState((prevState) => ({
              ...prevState,
              isLoaded: false,
              error,
            }));
          });
      }
    },
    [appState.configData]
  );

  const onMessageReceived = useCallback(
    function onMessageReceived(e) {
      if (e.origin !== parentHost) {
        return;
      }

      const message = e.data;
      const { action, payload } = message;

      if (action === "lgp:tcpa:open" && payload.url) {
        try {
          fetchContent(payload.url);
        } catch (error) {
          setAppState((prevState) => ({
            ...prevState,
            isLoaded: false,
            error,
          }));
        }
      } else if (action === "lgp:tcpa:close") {
        setAppState((prevState) => ({
          ...prevState,
          isLoaded: false,
          error: null,
        }));
      }
    },
    [parentHost, fetchContent]
  );

  useEffect(
    function onMessageReceivedEffect() {
      window.addEventListener("message", onMessageReceived);
      return () => window.removeEventListener("message", onMessageReceived);
    },
    [onMessageReceived]
  );

  useEffect(
    function errorHandlerEffect() {
      if (appState.error) {
        window.top.postMessage(
          {
            action: "lgp:tcpa:close",
          },
          "*"
        );

        console.error(appState.error);
      }
    },
    [appState.error]
  );

  return (
    <Provider
      value={{
        appState,
      }}
    >
      {children}
    </Provider>
  );
};

export default AppProvider;
